import { fbt } from 'fbtee'
import { toast } from 'sonner'
import { Heart, MoreHorizontal, Plus, Trash2 } from 'lucide-react'
import { cn } from '../../lib/utils'
import { Button } from '../ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'

/**
 * The trailing control on a profile artist row. On your own profile every row
 * is an artist you follow, so a heart says nothing: removing goes through a
 * menu (it also stops set auto-seeding for that artist) and offers an undo.
 * On someone else's profile the heart is yours — tap to follow what they like.
 */
export function ProfileArtistAction({
  isSelf,
  mine,
  artistName,
  onSet,
}: {
  isSelf: boolean
  mine: boolean
  artistName: string
  onSet: (marked: boolean) => void
}) {
  if (isSelf && mine) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={fbt('Artist options', 'Accessible label of the menu button on a profile artist row')}
          >
            <MoreHorizontal className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            variant="destructive"
            onSelect={() => {
              onSet(false)
              toast(
                fbt(
                  'Removed ' + fbt.param('artist', artistName),
                  'Toast after removing an artist from your profile',
                ).toString(),
                {
                  action: {
                    label: fbt('Undo', 'Toast button that re-adds an artist just removed from your profile').toString(),
                    onClick: () => onSet(true),
                  },
                },
              )
            }}
          >
            <Trash2 />
            {fbt('Remove from my artists', 'Menu item on a profile artist row that unfollows the artist')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  if (isSelf) {
    return (
      <Button
        variant="ghost"
        size="icon-sm"
        onClick={() => onSet(true)}
        aria-label={fbt('Add back to my artists', 'Accessible label of the button that re-adds a just-removed artist on your profile')}
      >
        <Plus className="size-4" />
      </Button>
    )
  }

  return (
    <Button
      variant="ghost"
      size="icon-sm"
      onClick={() => onSet(!mine)}
      aria-pressed={mine}
      aria-label={fbt('Follow artist', 'Accessible label of the heart on an artist row on someone else’s profile')}
    >
      <Heart className={cn('size-4', mine ? 'fill-interested text-interested' : 'text-muted-foreground')} />
    </Button>
  )
}
